import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Table,
} from "reactstrap";
import MetaTags from "react-meta-tags";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams } from "react-router-dom";
import { AxiosResponse } from "axios";

import Breadcrumbs from "../../components/common/Breadcrumb";
import { ApiCore } from "../../helpers/api_helper";
import { Loading } from "../../components/common/Loading";
import { Endpoints } from "../../utils/Endpoints";
import { fullUrl } from "../../utils/Helpers";
import StudentProfileCard from "./StudentProfileCard";

interface SummaryRecord {
  year: number;
  term: string;
}

interface FormResults {
  form: string;
  summaryRecords: SummaryRecord[];
}

interface MarkRecord {
  subject: string;
  mark: number;
  score: string;
  comment: string;
}

interface ResultGroup {
  label: string;
  marks: MarkRecord[];
}

interface CommentRecord {
  name: string;
  title: string;
  date: string;
  comment: string;
}

interface ResultDetail {
  pdf?: string | null;
  nc_pdf?: string | null;
  nc_results?: any[];
  type?: string;
  results?: ResultGroup[];
  comments?: CommentRecord[];
}

const request = new ApiCore();

const StudentResults = () => {
  const { id } = useParams() as { id: string };

  const [loadingStudent, setLoadingStudent] = useState<boolean>(true);
  const [loadingResults, setLoadingResults] = useState<boolean>(true);
  const [student, setStudent] = useState<any>(null);
  const [results, setResults] = useState<FormResults[]>([]);

  const [selected, setSelected] = useState<{ form: string; year: number; term: string } | null>(null);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [details, setDetails] = useState<ResultDetail[]>([]);

  useEffect(() => {
    request
      .get(`${Endpoints.students}/${id}`, {})
      .then((response: AxiosResponse) => {
        setStudent(response.data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingStudent(false);
      });
  }, [id]);

  useEffect(() => {
    request
      .get(Endpoints.results, { params: { student_id: id } })
      .then((response: AxiosResponse) => {
        setResults(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingResults(false);
      });
  }, [id]);

  const studentName = student
    ? `${student.firstname || ""} ${student.lastname || ""}`.trim() || student.name
    : "";

  const openDetails = (form: string, record: SummaryRecord) => {
    setSelected({ form, year: record.year, term: record.term });
    setDetails([]);
    setLoadingDetails(true);

    request
      .get(Endpoints.resultsDetails, {
        params: {
          student_id: id,
          form,
          year: record.year,
          term: record.term,
        },
      })
      .then((response: AxiosResponse) => {
        setDetails(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingDetails(false);
      });
  };

  const closeDetails = () => {
    setSelected(null);
    setDetails([]);
    setLoadingDetails(false);
  };

  const hasDetails =
    details.length > 0 &&
    details.some(
      (d) =>
        (d.results && d.results.length > 0) ||
        (d.comments && d.comments.length > 0) ||
        !!d.pdf ||
        !!d.nc_pdf
    );

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Student Results</title>
        </MetaTags>

        <style>{`
          .result-tile {
            transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
            cursor: pointer;
            border-left: 4px solid var(--bs-primary) !important;
          }
          .result-tile:hover {
            transform: translateY(-2px);
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.075);
            border-color: var(--bs-primary) !important;
          }
        `}</style>

        <Container fluid>
          <Breadcrumbs title="Students" breadcrumbItem="Results" />
          <ToastContainer />

          <Row>
            <Col md={12}>
              <StudentProfileCard loading={loadingStudent} student={student} />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Card>
                <CardHeader>
                  <CardTitle className="h5 mb-0">
                    {loadingStudent ? "Results" : `Results for ${studentName}`}
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  {loadingResults && <Loading label="Loading results..." />}
                  {!loadingResults && results.length === 0 && (
                    <p className="text-muted mb-0">No results available.</p>
                  )}
                  {!loadingResults &&
                    results.map((formResults) => (
                      <Card key={formResults.form} className="mb-4 border">
                        <CardHeader className="bg-light d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-graduation-cap text-primary me-2"></i>
                            <CardTitle className="h6 mb-0">{formResults.form}</CardTitle>
                          </div>
                          <small className="text-muted">
                            {formResults.summaryRecords.length} report
                            {formResults.summaryRecords.length === 1 ? "" : "s"}
                          </small>
                        </CardHeader>
                        <CardBody>
                          <Row>
                            {formResults.summaryRecords.map((record) => (
                              <Col
                                md={4}
                                lg={3}
                                key={`${formResults.form}-${record.year}-${record.term}`}
                                className="mb-3"
                              >
                                <Card
                                  className="h-100 border result-tile"
                                  onClick={() => openDetails(formResults.form, record)}
                                >
                                  <CardBody className="d-flex align-items-center">
                                    <i className="fas fa-calendar-alt text-muted me-3"></i>
                                    <h6 className="mb-0">
                                      {record.year} &middot;{" "}
                                      <span className="text-muted fw-normal">
                                        Term {record.term}
                                      </span>
                                    </h6>
                                  </CardBody>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </CardBody>
                      </Card>
                    ))}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <Modal isOpen={!!selected} toggle={closeDetails} size="xl" centered scrollable>
          <ModalHeader toggle={closeDetails}>
            {selected
              ? `${selected.form} · ${selected.year} · Term ${selected.term}`
              : "Result details"}
          </ModalHeader>
          <ModalBody>
            {loadingDetails && <Loading label="Loading details..." />}
            {!loadingDetails && !hasDetails && (
              <p className="text-muted mb-0">No details available.</p>
            )}
            {!loadingDetails &&
              hasDetails &&
              details.map((detail, detailIndex) => (
                <div key={detailIndex} className="mb-4">
                  {(detail.pdf || detail.nc_pdf) && (
                    <div className="d-flex justify-content-end mb-3 gap-2">
                      {detail.pdf && (
                        <a
                          href={fullUrl(detail.pdf)}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="btn btn-sm btn-outline-primary"
                        >
                          <i className="fas fa-file-pdf me-1"></i> Download PDF
                        </a>
                      )}
                      {detail.nc_pdf && (
                        <a
                          href={fullUrl(detail.nc_pdf)}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="btn btn-sm btn-outline-primary ms-2"
                        >
                          <i className="fas fa-file-pdf me-1"></i> Download NC PDF
                        </a>
                      )}
                    </div>
                  )}

                  {detail.results && detail.results.length > 0 && (
                    <>
                      {detail.results.map((group, groupIndex) => (
                        <Card key={groupIndex} className="mb-3 border">
                          <CardHeader className="bg-light">
                            <CardTitle className="h6 mb-0">{group.label}</CardTitle>
                          </CardHeader>
                          <CardBody className="p-0">
                            <Table responsive className="mb-0 align-middle">
                              <thead className="table-light">
                                <tr>
                                  <th>Subject</th>
                                  <th className="text-end">Mark</th>
                                  <th>Score</th>
                                  <th>Comment</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.marks.map((mark, markIndex) => (
                                  <tr key={markIndex}>
                                    <td className="fw-semibold">{mark.subject}</td>
                                    <td className="text-end">{mark.mark}</td>
                                    <td>
                                      <span className="badge bg-light text-dark border">
                                        {mark.score}
                                      </span>
                                    </td>
                                    <td className="text-muted">{mark.comment}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </CardBody>
                        </Card>
                      ))}
                    </>
                  )}

                  {detail.comments && detail.comments.length > 0 && (
                    <Card className="border">
                      <CardHeader className="bg-light">
                        <CardTitle className="h6 mb-0">Comments</CardTitle>
                      </CardHeader>
                      <CardBody>
                        {detail.comments.map((comment, commentIndex) => (
                          <div
                            key={commentIndex}
                            className={`pb-3 ${
                              commentIndex < (detail.comments?.length || 0) - 1
                                ? "mb-3 border-bottom"
                                : ""
                            }`}
                          >
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <div>
                                <div className="fw-semibold">{comment.title}</div>
                                <small className="text-muted">{comment.name}</small>
                              </div>
                              <small className="text-muted">{comment.date}</small>
                            </div>
                            <div>{comment.comment}</div>
                          </div>
                        ))}
                      </CardBody>
                    </Card>
                  )}
                </div>
              ))}
          </ModalBody>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default StudentResults;
